/**
 * 后台未读提醒：当 widget 收到「对方」新消息(AI 回答 / 真人客服回复)且访客当前
 * 没在看本页(浏览器最小化、切到别的标签、或切到别的应用窗口)时,通过以下方式提醒:
 *   ① 标题闪烁     —— 宿主标签页 document.title 在「💬 新消息(N)」与原标题间交替(核心,零授权)
 *   ② 系统通知     —— Notification API 弹系统横幅,使任务栏图标高亮(需用户授权)
 *   ③ favicon 角标 —— 标签页图标换成带红点的提示图标
 *   ④ 提示音       —— WebAudio 合成的短「叮咚」(需用户先与页面交互过解锁)
 * 访客切回本页(visibilitychange / window focus)即全部复位:停闪、还原标题与 favicon、关通知。
 *
 * widget 以 Shadow DOM 注入宿主同一文档(非 iframe),故直接读写 document.title / favicon
 * 即作用于宿主标签页,无需跨边界通信。
 *
 * 单一收口:订阅全局 messages signal,捕获「最后一条对方消息」id 变化即新消息,
 * 覆盖 web / digital / IM 三条线,无需改各 SSE 监听点。访客自己发的(role='visitor')、
 * 历史回填(hydrateFromHistory,ts 为过去时间)均被排除。
 */
import { effect } from '@preact/signals';
import { messages, currentLocale, opened } from '../store/widget-store';
import { createI18n } from '../i18n';
import type { UIMessage } from '../types';
import { dcsLog } from './logger';

// 增强项开关(标题闪烁恒为核心,无开关)。如需按后台配置下发,可改为读 config.value。
const ENABLE_NOTIFICATION = true;
const ENABLE_FAVICON_BADGE = true;
const ENABLE_SOUND = true;

/** 提示音两次触发的最小间隔(ms):一波密集消息只响一次,避免吵 */
const SOUND_THROTTLE_MS = 3000;

// ─────────────────────────── 公共:可见性判定 ───────────────────────────

/** 访客是否「没在看本页」:页面隐藏(最小化/切标签) 或 窗口失焦(切到别的应用)。 */
function isAway(): boolean {
  if (typeof document === 'undefined') return false;
  // 页面隐藏(最小化 / 切到别的标签):用 visibilityState(比 document.hidden 更标准,iOS Safari 表现更稳)
  if (document.visibilityState === 'hidden') return true;
  // 窗口失焦(切到别的应用窗口,但本标签仍可见)
  if (typeof document.hasFocus === 'function' && !document.hasFocus()) return true;
  return false;
}

// 未读计数(标题/通知共享);切回本页清零。
let unread = 0;

// ─────────────────────────── ① 标题闪烁 ───────────────────────────

let titleBase: string | null = null; // 进入闪烁前的真实标题(还原用)
let titleLastSet: string | null = null; // 我们最后写入的标题(判断宿主是否中途改过)
let titleTimer: number | null = null;
let titleAlt = false;

function buildFlashTitle(): string {
  const hint = createI18n(currentLocale.value).t('panel.newMessageTitle');
  return unread > 1 ? `${hint} (${unread})` : hint;
}

function titleTick(): void {
  // 宿主(如 SPA 路由切换)在两拍之间改了 title → 采纳为新基线,
  // 避免下面用陈旧 titleBase 反复覆盖宿主的新标题。
  if (titleLastSet !== null && document.title !== titleLastSet) {
    titleBase = document.title;
  }
  titleAlt = !titleAlt;
  const next = titleAlt ? buildFlashTitle() : titleBase ?? document.title;
  titleLastSet = next;
  document.title = next;
}

function startTitleFlash(): void {
  if (titleTimer != null) {
    // 已在闪 —— 仅刷新一次提示态(未读数已变)
    titleAlt = false;
    titleTick();
    return;
  }
  titleBase = document.title; // 此刻还没被我们改,记真实标题
  titleAlt = false;
  titleTick(); // 立即进入提示态,不等首个 interval
  titleTimer = window.setInterval(titleTick, 1000);
}

function stopTitleFlash(): void {
  if (titleTimer != null) {
    window.clearInterval(titleTimer);
    titleTimer = null;
  }
  titleAlt = false;
  // 仅当标题仍是我们写入的值才还原;若宿主中途改过(SPA 路由切 title),尊重宿主不覆盖
  if (titleBase != null && document.title === titleLastSet) {
    document.title = titleBase;
  }
  titleBase = null;
  titleLastSet = null;
}

// ─────────────────────────── ② favicon 红点角标 ───────────────────────────

let faviconLink: HTMLLinkElement | null = null;
let faviconOrigHref: string | null = null;
let faviconCreated = false; // 该 <link> 是否由我们新建(还原时直接移除)
let faviconApplied = false;

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** 同步自绘「对话气泡 + 红点」favicon(不读宿主原图,规避跨域 canvas 污染与异步竞态)。 */
function buildBadgeFavicon(): string | null {
  try {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // 蓝底圆
    ctx.fillStyle = '#2563eb';
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();
    // 白色对话气泡
    ctx.fillStyle = '#ffffff';
    const bx = size * 0.2;
    const by = size * 0.24;
    const bw = size * 0.6;
    const bh = size * 0.36;
    roundRect(ctx, bx, by, bw, bh, size * 0.1);
    ctx.fill();
    // 气泡尾巴
    ctx.beginPath();
    ctx.moveTo(bx + bw * 0.22, by + bh - 1);
    ctx.lineTo(bx + bw * 0.22, by + bh + size * 0.13);
    ctx.lineTo(bx + bw * 0.48, by + bh - 1);
    ctx.closePath();
    ctx.fill();
    // 右上角红点
    const r = size * 0.2;
    const cx = size - r - 2;
    const cy = r + 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#ef4444';
    ctx.fill();
    ctx.lineWidth = size * 0.05;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

function applyFaviconBadge(): void {
  if (faviconApplied) return;
  const dataUrl = buildBadgeFavicon();
  if (!dataUrl) return;
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (link) {
    faviconOrigHref = link.getAttribute('href');
    faviconCreated = false;
  } else {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
    faviconCreated = true;
    faviconOrigHref = null;
  }
  faviconLink = link;
  link.setAttribute('href', dataUrl);
  faviconApplied = true;
}

function restoreFavicon(): void {
  if (!faviconApplied) return;
  if (faviconCreated) {
    faviconLink?.remove();
  } else if (faviconLink && document.contains(faviconLink)) {
    // 仅当我们改过的那个 link 仍在 DOM 时才还原;若宿主 SPA 已删除/替换它,
    // 说明宿主已接管 favicon,尊重宿主不干预(强行重设会覆盖宿主的新图标)。
    if (faviconOrigHref !== null) faviconLink.setAttribute('href', faviconOrigHref);
    else faviconLink.removeAttribute('href');
  }
  faviconApplied = false;
  faviconLink = null;
  faviconOrigHref = null;
  faviconCreated = false;
}

// ─────────────────────────── ③ 系统桌面通知 ───────────────────────────

let activeNotification: Notification | null = null;

function showNotification(): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    const i18n = createI18n(currentLocale.value);
    if (activeNotification) {
      try {
        activeNotification.close();
      } catch {
        /* noop */
      }
    }
    const n = new Notification(i18n.t('panel.newMessageTitle'), {
      body: i18n.t('panel.newMessageBody'),
      tag: 'dcs-unread', // 合并为同一条,避免多条新消息堆叠成一排通知
      silent: ENABLE_SOUND, // 已自带提示音时让系统通知静音,避免双响
    });
    n.onclick = () => {
      try {
        window.focus();
      } catch {
        /* noop */
      }
      try {
        n.close();
      } catch {
        /* noop */
      }
    };
    activeNotification = n;
  } catch {
    /* 部分浏览器后台禁止新建通知,忽略 */
  }
}

function closeNotification(): void {
  if (!activeNotification) return;
  try {
    activeNotification.close();
  } catch {
    /* noop */
  }
  activeNotification = null;
}

function requestNotifyPermission(): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'default') return;
  try {
    const r = Notification.requestPermission();
    if (r && typeof r.then === 'function') {
      r.then((p) => dcsLog.info('notify', `通知授权结果: ${p}`)).catch(() => {
        /* noop */
      });
    }
  } catch {
    /* 旧版 callback 签名 / 被策略禁用,忽略 */
  }
}

// ─────────────────────────── ④ 提示音(WebAudio 合成) ───────────────────────────

let audioCtx: AudioContext | null = null;
let lastBeepTs = 0;

function getAudioCtx(): AudioContext | null {
  const Ctor =
    typeof AudioContext !== 'undefined'
      ? AudioContext
      : (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) {
    try {
      audioCtx = new Ctor();
    } catch {
      return null;
    }
  }
  return audioCtx;
}

function unlockAudio(): void {
  const ctx = getAudioCtx();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {
    /* noop */
  });
}

function playBeep(): void {
  const now = Date.now();
  if (now - lastBeepTs < SOUND_THROTTLE_MS) return;
  const ctx = getAudioCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {
      /* noop */
    });
  }
  try {
    const t0 = ctx.currentTime;
    const tone = (freq: number, start: number, dur: number): void => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t0 + start);
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + start);
      osc.stop(t0 + start + dur + 0.03);
    };
    tone(880, 0, 0.18); // 叮
    tone(1175, 0.15, 0.22); // 咚
    lastBeepTs = now;
  } catch {
    /* noop */
  }
}

// ─────────────────────────── 首次用户手势解锁 ───────────────────────────

let gestureBound = false;

/**
 * 访客打开过客服面板后,在其后续首次手势(pointerdown / keydown)时:
 *  - resume AudioContext(满足浏览器 autoplay 策略,后台才能出提示音)
 *  - 请求通知授权(满足「授权必须在用户手势内」的要求,且只对真正用过客服的访客请求)
 */
function bindFirstGesture(): void {
  if (gestureBound) return;
  gestureBound = true;
  const handler = (): void => {
    if (ENABLE_SOUND) unlockAudio();
    if (ENABLE_NOTIFICATION) requestNotifyPermission();
    document.removeEventListener('pointerdown', handler, true);
    document.removeEventListener('keydown', handler, true);
    document.removeEventListener('click', handler, true);
  };
  // click 对 iOS Safari 的 user activation 认可最稳;pointerdown/keydown 覆盖更早触发的手势。
  document.addEventListener('pointerdown', handler, true);
  document.addEventListener('keydown', handler, true);
  document.addEventListener('click', handler, true);
}

// ─────────────────────────── 装配 ───────────────────────────

/**
 * 安装后台未读提醒。在 main.tsx bootstrap() 内全局调用一次
 * (bootstrap 已有 dcsBootstrapped 防重,保证单实例)。
 */
export function installUnreadNotifier(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const installTs = Date.now();
  let lastInboundId: string | null = null;

  const fire = (): void => {
    unread += 1;
    startTitleFlash();
    if (ENABLE_FAVICON_BADGE) applyFaviconBadge();
    if (ENABLE_NOTIFICATION) showNotification();
    if (ENABLE_SOUND) playBeep();
  };

  const clearAll = (): void => {
    if (unread === 0 && titleTimer == null && !faviconApplied && !activeNotification) return;
    unread = 0;
    stopTitleFlash();
    if (ENABLE_FAVICON_BADGE) restoreFavicon();
    if (ENABLE_NOTIFICATION) closeNotification();
  };

  const onBackToView = (): void => {
    if (!isAway()) clearAll();
  };

  document.addEventListener('visibilitychange', onBackToView);
  window.addEventListener('focus', onBackToView);

  // 访客用过客服(打开过面板)后,首次手势解锁提示音 + 请求通知授权。
  effect(() => {
    if (opened.value) bindFirstGesture();
  });

  // 单一收口:订阅 messages,「最后一条对方消息」的 id 变化即一条新消息。
  effect(() => {
    const list = messages.value; // 订阅
    let last: UIMessage | undefined;
    for (let i = list.length - 1; i >= 0; i--) {
      const role = list[i].role;
      if (role === 'ai' || role === 'human') {
        last = list[i];
        break;
      }
    }
    if (!last) return;
    if (last.id === lastInboundId) return; // 同一条(AI 流式 delta 多次更新),非新消息
    lastInboundId = last.id;
    if (last.ts < installTs - 1000) return; // 历史回填(过去时间),不提醒
    if (!isAway()) return; // 访客正看着本页,不打扰
    fire();
  });
}
